import React from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';

/**
 * Default action button configurations
 */
const DEFAULT_ACTIONS = {
  history: {
    icon: HistoryIcon,
    tooltip: 'View History',
    color: 'primary',
  },
  edit: {
    icon: EditIcon,
    tooltip: 'Edit',
    color: 'primary',
  },
  duplicate: {
    icon: ContentCopyIcon,
    tooltip: 'Duplicate',
    color: 'primary',
  },
  delete: {
    icon: DeleteIcon,
    tooltip: 'Delete',
    color: 'error',
  },
};

/**
 * ActionsCellRenderer - Cell renderer for actions column
 *
 * @param {Object} props - AG Grid cell renderer props plus custom props
 * @param {Object} props.data - Row data from AG Grid
 * @param {Array<string|Object>} [props.actions] - Actions to show: 'history', 'edit', 'duplicate', 'delete'
 *   Can also be objects with custom config: { key: 'custom', icon: CustomIcon, tooltip: 'Custom', onClick: fn }
 * @param {Function} [props.onHistory] - Called when history is clicked
 * @param {Function} [props.onEdit] - Called when edit is clicked
 * @param {Function} [props.onDuplicate] - Called when duplicate is clicked
 * @param {Function} [props.onDelete] - Called when delete is clicked
 * @param {string} [props.size='small'] - IconButton size
 * @param {Function} [props.isDisabled] - Function to check if action is disabled: (action, data) => boolean
 *
 * @example
 * // In column definitions:
 * {
 *   headerName: 'Actions',
 *   field: 'actions',
 *   cellRenderer: ActionsCellRenderer,
 *   cellRendererParams: {
 *     actions: ['history', 'edit', 'delete'],
 *     onHistory: (data) => openHistoryModal(data),
 *     onEdit: (data) => openEditModal(data),
 *     onDelete: (data) => confirmDelete(data),
 *   },
 *   pinned: 'left',
 *   width: 150,
 *   sortable: false,
 *   filter: false,
 * }
 */
export const ActionsCellRenderer = ({
  data,
  actions = ['history', 'edit', 'duplicate', 'delete'],
  onHistory,
  onEdit,
  onDuplicate,
  onDelete,
  size = 'small',
  isDisabled,
}) => {
  if (!data) return null;

  const handlers = {
    history: onHistory,
    edit: onEdit,
    duplicate: onDuplicate,
    delete: onDelete,
  };

  const handleClick = (action, customHandler) => (event) => {
    event.stopPropagation();
    const handler = customHandler || handlers[action];
    if (handler) {
      handler(data);
    }
  };

  const renderAction = (action, index) => {
    // Handle string action keys
    if (typeof action === 'string') {
      const config = DEFAULT_ACTIONS[action];
      if (!config) return null;

      const Icon = config.icon;
      const disabled = isDisabled ? isDisabled(action, data) : false;

      return (
        <Tooltip key={action} title={config.tooltip}>
          <span>
            <IconButton
              size={size}
              color={config.color}
              onClick={handleClick(action)}
              disabled={disabled}
            >
              <Icon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      );
    }

    // Handle custom action objects
    if (typeof action === 'object') {
      const { key, icon: Icon, tooltip, color = 'primary', onClick } = action;
      const disabled = isDisabled ? isDisabled(key, data) : false;

      return (
        <Tooltip key={key || index} title={tooltip || ''}>
          <span>
            <IconButton
              size={size}
              color={color}
              onClick={handleClick(key, onClick)}
              disabled={disabled}
            >
              <Icon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      );
    }

    return null;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.5,
        height: '100%',
      }}
    >
      {actions.map(renderAction)}
    </Box>
  );
};

/**
 * Creates an actions column definition for AG Grid
 *
 * @param {Object} options - Column options
 * @param {Array<string|Object>} [options.actions] - Actions to include
 * @param {Function} [options.onHistory] - History handler
 * @param {Function} [options.onEdit] - Edit handler
 * @param {Function} [options.onDuplicate] - Duplicate handler
 * @param {Function} [options.onDelete] - Delete handler
 * @param {Function} [options.isDisabled] - Disable check function
 * @param {number} [options.width] - Column width (auto-calculated if not provided)
 * @param {string} [options.headerName='Actions'] - Column header
 * @param {boolean} [options.pinned='left'] - Pin position
 *
 * @returns {Object} AG Grid column definition
 *
 * @example
 * const columns = [
 *   createActionsColumn({
 *     actions: ['history', 'edit', 'delete'],
 *     onHistory: handleHistory,
 *     onEdit: handleEdit,
 *     onDelete: handleDelete,
 *   }),
 *   { field: 'name', headerName: 'Name' },
 *   // ... other columns
 * ];
 */
export const createActionsColumn = ({
  actions = ['history', 'edit', 'duplicate', 'delete'],
  onHistory,
  onEdit,
  onDuplicate,
  onDelete,
  isDisabled,
  width,
  headerName = 'Actions',
  pinned = 'left',
} = {}) => {
  // Calculate width based on number of actions (40px per action + padding)
  const calculatedWidth = width || Math.max(80, actions.length * 40 + 16);

  return {
    headerName,
    field: 'actions',
    cellRenderer: ActionsCellRenderer,
    cellRendererParams: {
      actions,
      onHistory,
      onEdit,
      onDuplicate,
      onDelete,
      isDisabled,
    },
    pinned,
    width: calculatedWidth,
    maxWidth: calculatedWidth + 20,
    minWidth: calculatedWidth - 10,
    sortable: false,
    filter: false,
    resizable: false,
    suppressHeaderMenuButton: true,
    cellStyle: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  };
};

export default ActionsCellRenderer;
